import {
  IEvent,
  IProgramResume,
  ISpeakerExtended,
} from "../../../interfaces/models/IEvent";
import WorldServicesGroupIcon from "../../../components/Atomos/Icons/WorldServicesGroupIcon";
import { capitalizeHeader } from "../../../utils/capitalize";
import { LinkifiedPlainText } from "../../../utils/LinkifiedPlainText";
import LocationIcon from "../../../assets/images&icons/location.png";
import IconLocation from "../../../assets/images&icons/Icon-Location.png";
import CalendarIcon from "../../../assets/images&icons/calendar.png";
import PlusIcon from "../../../assets/images&icons/plus.png";
import ProgramIcon from "../../../assets/images&icons/program-icon.png";
import { EventTypeEnum, SpeakerTypeEnum } from "../../../Enums/EventEnums";
import {
  formatEventLocation,
  getVenueDetails,
  formatVenueAddress,
} from "../../../utils/eventLocationFormatter";
import { formatEventDate } from "../../../utils/eventDateFormatter";
import { buildPersonProfilePath } from "../../../utils/publicProfileUrls";
import { useParams } from "react-router-dom";
import { useEventLoader } from "../../../hooks/Events/useEventLoader";
import CircularPhoto from "../../../components/Atomos/CircularPhoto/CircularPhoto";
import { useAddToCalendar } from "../../../hooks/Events/useAddToCalendar";
import { useEffect, useRef, useState } from "react";

/**
 * Extracts all unique speakers from all programs in an event.
 * @param {IEvent} event - The event containing programs with speakers.
 * @returns {ISpeaker[]} An array of unique speakers (deduplicated by personId).
 */
const getUniqueSpeakers = (event: IEvent): ISpeakerExtended[] => {
  if ((event as any)?.speakers && Array.isArray((event as any).speakers)) {
    return (event as any).speakers;
  }
  return [];
};

/**
 * Constructs the profile URL for a speaker.
 * @param {ISpeakerExtended} speaker - The speaker object.
 * @returns {string | null} The profile URL or null if legacyId is not available.
 */
const getSpeakerProfileUrl = (speaker: ISpeakerExtended): string | null => {
  if (!speaker.legacyId || !speaker.name || !speaker.lastName) {
    return null;
  }
  return buildPersonProfilePath(
    speaker.name,
    speaker.lastName,
    speaker.legacyId
  );
};

export default function MemberOverviewTab() {
  const { id } = useParams<{ id: string }>();
  const event = useEventLoader(id);
  const handleAddToCalendar = useAddToCalendar(event || undefined);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [maxTabHeight, setMaxTabHeight] = useState<number | null>(null);

  const uniqueSpeakers = getUniqueSpeakers(event as IEvent);
  const eventType = event?.eventType.toLowerCase();

  const isPastEvent =
    event?.isPastEvent === true &&
    event?.eventHostSponsor !== "group" &&
    event?.eventHostSponsor !== "groupMember";

  const shouldShowProgramTopic = () => {
    return (
      eventType === EventTypeEnum.virtual.toLowerCase() ||
      eventType === EventTypeEnum.hybrid.toLowerCase()
    );
  };

  const shouldShowTopicDescription = () => {
    return (
      eventType === EventTypeEnum.virtual.toLowerCase() ||
      eventType === EventTypeEnum.hybrid.toLowerCase()
    );
  };

  const shouldShowSpeakers = () => {
    return (
      eventType === EventTypeEnum.virtual.toLowerCase() ||
      eventType === EventTypeEnum.hybrid.toLowerCase()
    );
  };

  const shouldShowProgramAgenda = () => {
    return (
      eventType === EventTypeEnum.hybrid.toLowerCase() ||
      eventType === EventTypeEnum.inPerson.toLowerCase().replace(" ", "")
    );
  };

  const shouldShowProgramAgendaInDetails = () => {
    return eventType === EventTypeEnum.hybrid.toLowerCase();
  };

  const shouldShowEventTime = () => {
    return (
      event?.setupType === "cocktail" ||
      event?.setupType === "luncheon" ||
      event?.setupType === "webinar" ||
      event?.setupType === "groupWebinar"
    );
  };

  const formatTimeRange = (startTimestamp: number, endTimestamp: number) => {
    const startDate = new Date(startTimestamp * 1000);
    const endDate = new Date(endTimestamp * 1000);

    const startTimeWithPeriod = startDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });

    const endTimeWithPeriod = endDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });

    const startPeriod = startTimeWithPeriod.includes("AM") ? "AM" : "PM";
    const endPeriod = endTimeWithPeriod.includes("AM") ? "AM" : "PM";

    const startTime = startTimeWithPeriod.replace(/\s*(AM|PM)/i, "");
    const endTime = endTimeWithPeriod.replace(/\s*(AM|PM)/i, "");

    if (startPeriod === endPeriod) {
      return `${startTime} - ${endTime} ${endPeriod}`;
    }

    return `${startTime} ${startPeriod} - ${endTime} ${endPeriod}`;
  };

  const getLocationDisplay = () => {
    return formatEventLocation(event);
  };

  const addToCalendarButton = !event?.comingSoonDate ? (
    <div
      className="flex items-center gap-2 md:gap-3 cursor-pointer hover:opacity-80 transition-opacity"
      onClick={handleAddToCalendar}
    >
      <img
        src={PlusIcon}
        alt="Add to calendar"
        className="w-4 h-4 md:w-6 md:h-6 lg:w-9 lg:h-8 text-[#C2C2C2] flex-shrink-0"
      />
      <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
        Add to calendar
      </span>
    </div>
  ) : null;

  const comingSoonLocationDisplay = event?.comingSoonLocation ? (
    <div className="flex items-center gap-2 md:gap-3">
      <img
        src={IconLocation}
        alt="Location"
        className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0"
      />
      <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
        {event.comingSoonLocationMessage || "Coming Soon Location"}
      </span>
    </div>
  ) : null;

  const formatSessionDate = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    const day = date.getUTCDate();
    const month = date.toLocaleDateString("en-US", {
      month: "long",
      timeZone: "UTC",
    });
    const year = date.getUTCFullYear();
    const weekkDay = date.toLocaleDateString("en-US", {
      weekday: "long",
      timeZone: "UTC",
    });
    return `${weekkDay}, ${month} ${day}, ${year}`;
  };

  const getSessions = (): IProgramResume[] => {
    return (
      event?.overviewProgram?.filter(
        (session) =>
          session.title && session.title.trim() !== "" && session.date
      ) || []
    );
  };

  const pastEventTabs = [
    {
      label: "Details",
      content: (
        <div className="space-y-2 md:space-y-3">
          <div className="flex items-center gap-2 md:gap-3">
            <WorldServicesGroupIcon className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0" />
            <span className="text-primary font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
              {formatEventLocation(event)}
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <img
              src={CalendarIcon}
              alt="Event date"
              className="w-4 h-6 md:w-6 md:h-8 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0"
            />
            <div className="flex flex-col">
              <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                {formatEventDate(event)}
              </span>
              {shouldShowEventTime() && event?.startDate && event?.endDate && (
                <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                  {formatTimeRange(event.startDate, event.endDate)}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      label: "Venue",
      content: (
        <div className="space-y-2 md:space-y-3">
          {event?.comingSoonLocation ? (
            comingSoonLocationDisplay
          ) : (
            event?.address && (
            <>
              {event.address.address1 && (
                <div className="flex items-center gap-2 md:gap-3">
                  <img
                    src={IconLocation}
                    alt="Address"
                    className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0"
                  />
                  {event?.venueLink ? (
                    <a
                      href={event.venueLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-Default font-sans text-[25px] font-normal leading-4 md:leading-5 lg:leading-6 break-words hover:underline hover:text-primary cursor-pointer"
                    >
                      {event.address.address1}
                    </a>
                  ) : (
                    <span className="text-gray-font font-sans text-[25px] font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                      {event.address.address1}
                    </span>
                  )}
                </div>
              )}
              {(event.address.address2 ||
                event.address.cityName ||
                event.address.countryName) && (
                <div className="flex items-start gap-2 md:gap-3">
                  <span className="w-4 h-6 md:w-6 md:h-8 lg:w-8 lg:h-10 flex-shrink-0"></span>
                  <div className="flex flex-col gap-1">
                    {event.address.address2 && (
                      <span className="text-gray-font font-sans text-base lg:text-lg font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                        {event.address.address2}
                      </span>
                    )}
                    {(event.address.cityName ||
                      event.address.stateName ||
                      event.address.zipCode ||
                      event.address.countryName) && (
                      <span className="text-gray-font font-sans text-base lg:text-lg font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                        {[
                          event.address.cityName,
                          event.address.stateName,
                          event.address.zipCode || null,
                          event.address.countryName,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </>
            )
          )}
          {addToCalendarButton}
        </div>
      ),
    },
  ];

  const cocktailTabs = [
    {
      label: "Details",
      content: (
        <div className="space-y-2 md:space-y-3">
          {event?.setupType === "webinar" ||
          event?.setupType === "groupWebinar" ? (
            <div className="flex items-center gap-2 md:gap-3">
              <WorldServicesGroupIcon className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0" />
              <span className="text-primary font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                Virtual
              </span>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 md:gap-3">
                <WorldServicesGroupIcon className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0" />
                <span className="text-primary font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                  {getLocationDisplay()}
                </span>
              </div>
              {event?.eventType === "hybrid" && (
                <div className="flex items-center gap-2 md:gap-3">
                  <WorldServicesGroupIcon className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0" />
                  <span className="text-primary font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                    Virtual
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 md:gap-3">
                <img
                  src={CalendarIcon}
                  alt="Event type"
                  className="w-4 h-6 md:w-6 md:h-8 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0"
                />
                <div className="flex flex-col">
                  <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                    {formatEventDate(event)}
                  </span>
                  {shouldShowEventTime() &&
                    event?.startDate &&
                    event?.endDate && (
                      <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                        {formatTimeRange(event.startDate, event.endDate)}
                      </span>
                    )}
                </div>
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      label: "Venue",
      content: (
        <div className="space-y-2 md:space-y-3">
          {event?.comingSoonLocation ? (
            comingSoonLocationDisplay
          ) : (
            (() => {
              const venueDetails = getVenueDetails(event);
              if (venueDetails) {
                return (
                  <>
                    {venueDetails.location && (
                      <div className="flex items-start gap-2 md:gap-3">
                        <img
                          src={IconLocation}
                          alt="Location"
                          className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0"
                        />
                        <div className="flex flex-col">
                          {venueDetails.venueLink ? (
                            <a
                              href={venueDetails.venueLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-bold leading-4 md:leading-5 lg:leading-6 break-words hover:underline"
                            >
                              {venueDetails.location}
                            </a>
                          ) : (
                            <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-bold leading-4 md:leading-5 lg:leading-6 break-words">
                              {venueDetails.location}
                            </span>
                          )}
                          {formatVenueAddress(venueDetails) && (
                            <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                              {formatVenueAddress(venueDetails)}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                );
              }
              return null;
            })()
          )}

          {addToCalendarButton}
        </div>
      ),
    },
  ];

  const regularTabs = [
    {
      label: "Where",
      content: (
        <div className="space-y-2 md:space-y-3">
          {event?.setupType === "webinar" ||
          event?.setupType === "groupWebinar" ? (
            <div className="flex items-center gap-2 md:gap-3">
              <WorldServicesGroupIcon className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0" />
              <span className="text-primary font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                Virtual
              </span>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 md:gap-3">
                <WorldServicesGroupIcon className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0" />
                <span className="text-primary font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                  {getLocationDisplay()}
                </span>
              </div>
              {event?.eventType === "hybrid" && (
                <div className="flex items-center gap-2 md:gap-3">
                  <WorldServicesGroupIcon className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0" />
                  <span className="text-primary font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                    Virtual
                  </span>
                </div>
              )}
              {!event?.comingSoonLocation &&
                (() => {
                  const venueDetails = getVenueDetails(event);
                  if (venueDetails?.location === "Coming Soon") {
                    return null;
                  }
                  if (venueDetails) {
                    return (
                      <>
                        {venueDetails.location && (
                          <div className="flex items-start gap-2 md:gap-3">
                            <span className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 flex-shrink-0"></span>
                            <div className="flex flex-col">
                              {venueDetails.venueLink ? (
                                <a
                                  href={venueDetails.venueLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words hover:underline"
                                >
                                  {venueDetails.location}
                                </a>
                              ) : (
                                <span className="text-primary font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                                  {venueDetails.location}
                                </span>
                              )}
                              {formatVenueAddress(venueDetails) && (
                                <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                                  {formatVenueAddress(venueDetails)}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  }
                  return null;
                })()}
              {event?.eventType && event?.eventType !== "virtual" && (
                <div className="flex items-center gap-2 md:gap-3">
                  <img
                    src={LocationIcon}
                    alt="Event type"
                    className="w-4 h-6 md:w-6 md:h-8 lg:w-8 lg:h-10 text-[#C2C2C2] flex-shrink-0"
                  />
                  <span className="text-primary font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                    {capitalizeHeader(event?.eventType || "")}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      label: "When",
      content: (
        <div className="space-y-2 md:space-y-3">
          <div className="flex items-center gap-2 md:gap-3">
            <img
              src={CalendarIcon}
              alt="Event type"
              className="w-4 h-6 md:w-6 md:h-8 lg:w-8 lg:h-8 text-[#C2C2C2] flex-shrink-0"
            />
            <div className="flex flex-col">
              <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                {formatEventDate(event)}
              </span>
              {shouldShowEventTime() && event?.startDate && event?.endDate && (
                <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                  {formatTimeRange(event.startDate, event.endDate)}
                </span>
              )}
            </div>
          </div>
          {addToCalendarButton}
        </div>
      ),
    },
    {
      label: "Program",
      content: (
        <div className="space-y-0.5 lg:space-y-1 text-[17px] font-Roboto text-gray-font">
          {shouldShowProgramTopic() ? (
            <div className="flex items-start gap-2 md:gap-3">
              <img
                src={ProgramIcon}
                alt="Program"
                className="w-4 h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 flex-shrink-0"
              />
              {event?.programTopic ? (
                <div className="flex flex-col gap-2">
                  <p className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-bold leading-4 md:leading-5 lg:leading-6 break-words">
                    Topic
                  </p>
                  <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                    {event?.programTopic ||
                      "Program topic will be available soon."}
                  </span>
                </div>
              ) : (
                <span className="text-gray-font font-sans text-xs sm:text-sm md:text-base lg:text-xl font-normal leading-4 md:leading-5 lg:leading-6 break-words">
                  Program topic will be available soon.
                </span>
              )}
            </div>
          ) : shouldShowProgramAgenda() &&
            !shouldShowTopicDescription() &&
            !shouldShowSpeakers() ? (
            (() => {
              const sessions = getSessions();
              if (sessions.length === 0) {
                return "Program topic will be available soon.";
              }
              const hasThreeSessions = sessions.length === 3;
              return (
                <div className="w-full">
                  {sessions.map((session, index) => (
                    <div
                      key={index}
                      className="flex flex-col items-start border border-[#F4F4F4] rounded"
                    >
                      <p
                        className={`text-gray-font whitespace-nowrap font-semibold ${
                          hasThreeSessions
                            ? "text-[11px] sm:text-xs md:text-sm lg:text-base"
                            : "text-xs sm:text-sm md:text-base lg:text-xl"
                        }`}
                      >
                        {formatSessionDate(session.date)}
                      </p>
                      <h5
                        className={`text-gray-font flex-1 ${
                          hasThreeSessions
                            ? "text-[11px] sm:text-xs md:text-sm lg:text-base"
                            : "text-xs sm:text-sm md:text-base lg:text-xl"
                        }`}
                      >
                        {session.title}
                      </h5>
                    </div>
                  ))}
                </div>
              );
            })()
          ) : (
            "Program topic will be available soon."
          )}
        </div>
      ),
      shouldShow:
        event?.setupType !== "luncheon" &&
        (shouldShowProgramTopic() ||
          (shouldShowProgramAgenda() &&
            !shouldShowTopicDescription() &&
            !shouldShowSpeakers())),
    },
    {
      label: "Program Details",
      content: (
        <div className="block text-left flex-1 w-full overflow-hidden">
          {shouldShowTopicDescription() && (
            <div className="text-gray-font font-sans font-normal leading-4 md:leading-5 lg:leading-6 break-words px-4 md:px-8">
              <p className="mb-2 text-lg md:text-xl font-medium">
                {event?.programTopic ||
                  "Program details will be available soon."}
              </p>
              <p>
                {event?.topicDescription ||
                  "Program details will be available soon."}
              </p>
            </div>
          )}
          {shouldShowSpeakers() && (
            <div className="text-gray-font font-sans font-normal leading-4 md:leading-5 lg:leading-6 break-words">
              <div className="border-b border-primary pb-0 flex-shrink-0 mb-4 w-fit pl-4 md:pl-8">
                <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans mt-4">
                  {"Presenters"}
                </h3>
              </div>
              {uniqueSpeakers.length > 0 ? (
                <>
                  <div className="hidden lg:grid grid-cols-1 lg:grid-cols-3 gap-4 xl:gap-12 md:mr-4 pl-4 md:pl-8">
                    {uniqueSpeakers.map((speaker) => (
                      <div
                        key={speaker.personId}
                        className="flex items-center flex-col md:flex-row gap-4 lg:gap-8 xl:gap-10"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <CircularPhoto
                            personId={speaker.personId}
                            photo={speaker.photo}
                            alt={speaker.name}
                            containerClassName="mt-4 md:mt-0 mb-2 md:mb-0"
                          />
                          <p className="font-Roboto font-[400] text-sm md:text-base lg:text-lg text-[#474F56]">
                            {
                              SpeakerTypeEnum[
                                speaker.speakerType as keyof typeof SpeakerTypeEnum
                              ]
                            }
                          </p>
                        </div>
                        <div className="flex flex-col items-center md:items-start gap-2 md:gap-0">
                          {getSpeakerProfileUrl(speaker) ? (
                            <a
                              href={getSpeakerProfileUrl(speaker)!}
                              className="text-[#0070CD] text-left font-Roboto font-[600] text-lg lg:text-xl hover:text-red-600 hover:underline transition-colors"
                            >
                              {speaker.name}
                            </a>
                          ) : (
                            <p className="text-[#0070CD] text-left font-Roboto font-[600] text-lg lg:text-xl">
                              {speaker.name}
                            </p>
                          )}
                          <p className="text-[#474F56] text-left font-Roboto font-[400] text-sm md:mt-2">
                            {speaker.title ?? "Partner"}
                          </p>
                          <p className="text-[#474F56] text-left font-Roboto font-[400] text-sm">
                            {speaker.firmName ?? "Firm"}
                          </p>
                          <img
                            src={speaker.firmLogo}
                            alt={speaker.firmName}
                            className="max-h-10 w-auto md:mt-4"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="md:grid grid-cols-1 md:grid-cols-2 gap-4 mr-4 lg:hidden block pl-4 md:pl-8 mb-4">
                    {uniqueSpeakers.map((speaker) => (
                      <div
                        key={speaker.personId}
                        className="flex items-center flex-col md:flex-row gap-4 lg:gap-8 xl:gap-10"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <CircularPhoto
                            personId={speaker.personId}
                            photo={speaker.photo}
                            alt={speaker.name}
                            containerClassName="mt-4 md:mt-0 mb-2 md:mb-0"
                          />
                          <p className="font-Roboto font-[400] text-sm md:text-base lg:text-lg text-[#474F56]">
                            {
                              SpeakerTypeEnum[
                                speaker.speakerType as keyof typeof SpeakerTypeEnum
                              ]
                            }
                          </p>
                        </div>
                        <div className="flex flex-col items-center md:items-start gap-0">
                          {getSpeakerProfileUrl(speaker) ? (
                            <a
                              href={getSpeakerProfileUrl(speaker)!}
                              className="text-[#0070CD] text-left font-Roboto font-[600] text-lg lg:text-xl hover:text-red-600 hover:underline transition-colors"
                            >
                              {speaker.name}
                            </a>
                          ) : (
                            <p className="text-[#0070CD] text-left font-Roboto font-[600] text-lg lg:text-xl">
                              {speaker.name}
                            </p>
                          )}
                          <p className="text-[#474F56] text-left font-Roboto font-[400] text-sm">
                            {speaker.title ?? "Partner"}
                          </p>
                          <p className="text-[#474F56] text-left font-Roboto font-[400] text-sm">
                            {speaker.firmName ?? "Firm"}
                          </p>
                          <img
                            src={speaker.firmLogo}
                            alt={speaker.firmName}
                            className="max-h-10 w-auto mt-4"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-gray-font font-sans font-normal text-sm md:text-base pl-4 md:pl-8">
                  No presenters available.
                </p>
              )}
            </div>
          )}
          {shouldShowProgramAgendaInDetails() && (
            <div className="text-gray-font font-sans font-normal leading-4 md:leading-5 lg:leading-6 break-words">
              {(() => {
                const sessions = getSessions();
                if (sessions.length === 0) {
                  return !shouldShowTopicDescription() &&
                    !shouldShowSpeakers() ? null : (
                    <p className="mb-2 text-lg md:text-xl font-medium mt-4">
                      Program agenda will be available soon.
                    </p>
                  );
                }
                return (
                  <>
                    <div className="border-b border-primary pb-0 flex-shrink-0 mb-4 w-fit pl-4 md:pl-8">
                      <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans">
                        {"Program Agenda"}
                      </h3>
                    </div>
                    <div className="w-full px-4 md:px-8">
                      {sessions.map((session, index) => (
                        <div
                          key={index}
                          className="flex flex-row items-center gap-3 border border-[#F4F4F4] rounded"
                        >
                          <h5 className="text-base md:text-lg font-semibold text-gray-font flex-1">
                            {session.title}
                          </h5>
                          <p className="text-sm md:text-base text-gray-font whitespace-nowrap">
                            {formatSessionDate(session.date)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      ),
      shouldShow:
        event?.setupType !== "luncheon" &&
        (shouldShowTopicDescription() ||
          shouldShowSpeakers() ||
          shouldShowProgramAgendaInDetails()),
    },
  ];

  const tabs = isPastEvent
    ? pastEventTabs
    : event?.setupType === "cocktail" || event?.setupType === "luncheon"
    ? cocktailTabs
    : regularTabs;

  const filteredTabs = tabs.filter((tab) => {
    if (isPastEvent) {
      return true;
    }
    if (tab.label === "Program") {
      return (tab as any).shouldShow !== false;
    }
    if (tab.label === "Program Details") {
      return (tab as any).shouldShow !== false;
    }
    return true;
  });

  useEffect(() => {
    const calculateMaxHeight = () => {
      requestAnimationFrame(() => {
        const heights = tabRefs.current
          .slice(0, 3)
          .filter((ref) => ref !== null)
          .map((ref) => {
            if (!ref) {
              return 0;
            }
            const savedMinHeight = ref.style.minHeight;
            ref.style.minHeight = "";
            const naturalHeight = ref.offsetHeight;
            ref.style.minHeight = savedMinHeight;
            return naturalHeight;
          });

        if (heights.length > 0) {
          const maxHeight = Math.max(...heights);
          setMaxTabHeight((prev) => {
            if (prev === null || Math.abs(prev - maxHeight) > 2) {
              return maxHeight;
            }
            return prev;
          });
        }
      });
    };

    setMaxTabHeight(null);

    const timeoutId = setTimeout(calculateMaxHeight, 50);

    window.addEventListener("resize", calculateMaxHeight);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", calculateMaxHeight);
    };
  }, [filteredTabs.length, event?.id]);

  return (
    <div className="space-y-6 text-left">
      <div>
        <h1 className="text-[26px] md:text-[30px] lg:text-[32px] font-medium text-primary mb-4 leading-9 font-decimal text-left">
          Overview
        </h1>
        <div className="mt-6 text-left">
          <h2 className="text-xl md:text-[22px] font-semibold text-gray-font leading-6 mb-3 text-left w-full font-sans">
            {event?.title}
          </h2>
          <p className="whitespace-pre-line text-base lg:text-[18px] font-normal text-gray-font leading-normal mb-4 text-left font-sans">
            <LinkifiedPlainText
              text={event?.overviewTabDescription || event?.description || ""}
            />
          </p>
        </div>
      </div>
      <div className="flex ">
        <div className="flex gap-4">
          <div className="w-4 h-4 bg-primary rounded-full"></div>
          <div className="w-4 h-4 bg-primary rounded-full"></div>
          <div className="w-4 h-4 bg-primary rounded-full"></div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row flex-wrap gap-2">
        {filteredTabs.slice(0, 3).map((tab, index) => {
          const isDetailsOrVenueTab =
            tab.label === "Details" || tab.label === "Venue";

          return (
            <div
              key={index}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              className={`flex flex-col items-start h-auto pt-2 md:pt-4 pb-3 md:pb-6 px-0 bg-gray-15 transition-all text-left flex-1 min-w-full md:min-w-0 ${
                isDetailsOrVenueTab ? "overflow-visible" : "overflow-hidden"
              }`}
              style={
                maxTabHeight && index < 3
                  ? { minHeight: `${maxTabHeight}px` }
                  : undefined
              }
            >
              <div className="border-b border-primary pb-0 flex-shrink-0 mb-4">
                <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans pl-4 md:pl-8">
                  {tab.label}
                </h3>
              </div>
              <div
                className={`block text-left px-4 md:px-8 flex-1 w-full ${
                  isDetailsOrVenueTab ? "overflow-visible" : "overflow-hidden"
                }`}
              >
                {tab.content}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-col gap-2">
        {filteredTabs.slice(3).map((tab, index) => (
          <div
            key={index + 3}
            className={`flex flex-col items-start h-auto pt-2 md:pt-4 pb-3 md:pb-6 px-0 bg-gray-15 transition-all text-left w-full overflow-hidden`}
          >
            <div className="border-b border-primary pb-0 flex-shrink-0 mb-4">
              <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans pl-4 md:pl-8">
                {tab.label}
              </h3>
            </div>
            <div className="block text-left flex-1 w-full overflow-hidden">
              {tab.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
